/**
 * Esquemas de los mensajes MQTT, según docs/05-contrato-mqtt.md.
 *
 * Todo lo que llega del bus se valida antes de tocar la base. Un lector UHF es
 * un aparato barato en la calle: se asume que puede publicar cualquier cosa,
 * por error de firmware o porque alguien lo intervino.
 */

import { z } from 'zod';

/** ISO 8601. Los aparatos sin reloj lo omiten y el servidor sella la recepción. */
const marcaTiempo = z.string().datetime({ offset: true });

// ── 1. Lector UHF → servidor ──────────────────────────────────

export const eventoUhfSchema = z.object({
  /** EPC crudo. Kodica lo normaliza con `normalizarEpc` antes de comparar. */
  epc: z.string().min(1).max(128),
  rssi: z.number().int().optional(),
  antena: z.number().int().positive().optional(),
  ts: marcaTiempo.optional(),
});
export type EventoUhf = z.infer<typeof eventoUhfSchema>;

// ── 2. Servidor → controlador de pluma ────────────────────────

export const ACCIONES_PLUMA = ['abrir', 'cerrar', 'mantener_abierto', 'ping'] as const;

export const comandoPlumaSchema = z.object({
  accion: z.enum(ACCIONES_PLUMA),
  pulsoMs: z.number().int().positive().max(60_000).optional(),
  /** ULID que regresa en el acuse. Es la clave de la idempotencia. */
  solicitudId: z.string().min(1).max(64),
  ts: marcaTiempo,
});
export type ComandoPluma = z.infer<typeof comandoPlumaSchema>;

// ── 3. Controlador → servidor ─────────────────────────────────

export const respuestaPlumaSchema = z.object({
  solicitudId: z.string().min(1).max(64),
  resultado: z.enum(['ok', 'error']),
  detalle: z.string().max(500).optional(),
  ts: marcaTiempo.optional(),
});
export type RespuestaPluma = z.infer<typeof respuestaPlumaSchema>;

export const SENSORES_PASO = ['lazo_entrada', 'lazo_salida', 'fotocelda', 'masa_metalica'] as const;

export const EVENTOS_PLUMA = [
  'paso',
  'obstruccion',
  'pluma_forzada',
  'modo_manual',
  'bateria_baja',
] as const;

export const eventoPlumaSchema = z.object({
  evento: z.enum(EVENTOS_PLUMA),
  sensor: z.enum(SENSORES_PASO).optional(),
  /** Presente en `paso`: correlaciona el cruce con la apertura que lo causó. */
  solicitudId: z.string().max(64).optional(),
  ts: marcaTiempo.optional(),
});
export type EventoPluma = z.infer<typeof eventoPlumaSchema>;

// ── 4. Latido ─────────────────────────────────────────────────

/**
 * `passthrough`: cada fabricante agrega sus propios campos al latido y
 * rechazarlos no aporta nada. Lo que importa es que llegue.
 */
export const latidoSchema = z
  .object({
    uptimeS: z.number().int().nonnegative().optional(),
    version: z.string().max(64).optional(),
    temperaturaC: z.number().optional(),
  })
  .passthrough();
export type Latido = z.infer<typeof latidoSchema>;

// ── 5. Estado (LWT retenido) ──────────────────────────────────

export const estadoDispositivoSchema = z.object({
  online: z.boolean(),
});
export type EstadoDispositivo = z.infer<typeof estadoDispositivoSchema>;

// ── 6. Servidor → kiosco ──────────────────────────────────────

export const comandoKioscoSchema = z
  .object({
    accion: z.enum(['mensaje', 'recargar', 'reiniciar', 'ping']),
    texto: z.string().max(280).optional(),
    segundos: z.number().int().positive().max(300).optional(),
  })
  .refine((c) => c.accion !== 'mensaje' || !!c.texto, {
    message: 'la acción "mensaje" requiere el campo texto',
    path: ['texto'],
  });
export type ComandoKiosco = z.infer<typeof comandoKioscoSchema>;

// ── Constantes del contrato ───────────────────────────────────
//
// Viven aquí y no dispersas en el código porque son acuerdos con el hardware:
// cambiarlas es cambiar el contrato, y debe verse en un solo lugar.

/** Una lectura por EPC cada 3 s. El filtro del servidor va aunque el lector filtre. */
export const ANTIRREBOTE_EPC_MS = 3_000;

/** Sin acuse del controlador en 3 s, la apertura se marca fallida. */
export const TIMEOUT_ACUSE_PLUMA_MS = 3_000;

/** Ventana en la que el controlador debe ignorar un `solicitudId` repetido. */
export const IDEMPOTENCIA_SOLICITUD_MS = 30_000;

/** Cada aparato late cada 30 s... */
export const LATIDO_INTERVALO_MS = 30_000;

/** ...y a los 90 s sin latido aparece caído en el panel del guardia. */
export const LATIDO_TIMEOUT_MS = 90_000;

/**
 * QoS 1 en todo el bus: los mensajes importan más de lo que cuesta un
 * duplicado, y contra los duplicados ya está `solicitudId`.
 */
export const QOS_PREDETERMINADO = 1;
